import moment from 'moment';

import { SHIFT_TYPES } from '@src/packages/shifts/constants';
import { DateUtil } from '@src/packages/utils/dates';

import { WORK_JOURNEY_PLACE_TYPE } from '../constants';
import type { WorkJourneyForm } from '../types';

export interface WorkJourneyValidationTranslations {
  nameRequired: string;
  workShiftRequired: string;
  workShiftIdRequired: string;
  startDateRequired: string;
  supervisorsRequired: string;
  invalidRequiredMembers: string;
  locationRequired: string;
  locationTypeRequired: string;
  breakMarkingTypeRequired: string;
  breakStartTimeRequired: string;
  toleranceTimeInvalid: string;
  earlyEndTimeInvalid: string;
  shiftEndDateBeforeStart: string;
  journeyTypeRequired: string;
  workCircumstancesRequired: string;
  /** Used by WorkJourneyService member asserts (not form field validation). */
  membersMissingAddress?: string;
  membersPending?: string;
  membersInactive?: string;
}

export interface WorkJourneyValidationResult {
  isValid: boolean;
  errors: string[];
}

const VALID_LOCATION_TYPES = Object.values(WORK_JOURNEY_PLACE_TYPE);
const VALID_BREAK_MARKING_TYPES = ['manual', 'automatic'] as const;
const VALID_WORK_CIRCUMSTANCES = ['DEPENDS_ON_TIME_BAND', 'INDEPENDENT_OF_TIME_BAND'] as const;
const NUMERIC_FIELD_MIN = 0;
const NUMERIC_FIELD_MAX = 120;

const getDateMs = (value: unknown): number | null => {
  if (moment.isMoment(value)) {
    return value.isValid() ? value.valueOf() : null;
  }

  return DateUtil.getTimestampMs(value);
};

const isFiniteIntegerInRange = (value: unknown, min: number, max: number): boolean => {
  // Accept numbers directly and numeric strings (e.g. legacy data or values that
  // reach the form store as strings). Reject empty/whitespace so they don't coerce to 0.
  let numericValue: number;
  if (typeof value === 'number') {
    numericValue = value;
  } else if (typeof value === 'string' && value.trim() !== '') {
    numericValue = Number(value);
  } else {
    return false;
  }

  if (!Number.isFinite(numericValue)) {
    return false;
  }

  const truncated = Math.trunc(numericValue);
  return truncated === numericValue && numericValue >= min && numericValue <= max;
};

const hasWorkShiftId = (workShift: WorkJourneyForm['workShift']): boolean =>
  Boolean(workShift && typeof workShift === 'object' && workShift.id);

const requiresBreakStartTime = (type: string | null): boolean =>
  type === SHIFT_TYPES.FIXED || type === SHIFT_TYPES.PART_TIME;

const hasBreakStartTime = (breakStartTime: WorkJourneyForm['breakStartTime']): boolean => {
  if (breakStartTime == null) {
    return false;
  }

  if (typeof breakStartTime === 'string') {
    return breakStartTime.trim().length > 0;
  }

  return true;
};

const isValidRequiredMembers = (requiredMembers: unknown): requiredMembers is number =>
  typeof requiredMembers === 'number' &&
  Number.isFinite(requiredMembers) &&
  Number.isInteger(requiredMembers) &&
  requiredMembers >= 0;

export const validateWorkJourneyForm = (
  data: WorkJourneyForm,
  translations: WorkJourneyValidationTranslations,
): WorkJourneyValidationResult => {
  const errors: string[] = [];

  if (!data.name || data.name.trim().length === 0) {
    errors.push(translations.nameRequired);
  }

  if (!data.type) {
    errors.push(translations.journeyTypeRequired);
  }

  if (!data.workShift) {
    errors.push(translations.workShiftRequired);
  } else if (!hasWorkShiftId(data.workShift)) {
    errors.push(translations.workShiftIdRequired);
  }

  const startMs = getDateMs(data.shiftStartDate);
  if (startMs == null) {
    errors.push(translations.startDateRequired);
  }

  const endMs = getDateMs(data.shiftEndDate);
  if (startMs != null && endMs != null && endMs < startMs) {
    errors.push(translations.shiftEndDateBeforeStart);
  }

  if (!Array.isArray(data.supervisors) || data.supervisors.length === 0) {
    errors.push(translations.supervisorsRequired);
  }

  // requiredMembers is the planned headcount; assigned members are optional at
  // create/edit and can be filled later (understaffing is surfaced in the UI).
  if (!isValidRequiredMembers(data.requiredMembers)) {
    errors.push(translations.invalidRequiredMembers);
  }

  if (!data.locationType || !VALID_LOCATION_TYPES.includes(data.locationType as never)) {
    errors.push(translations.locationTypeRequired);
  } else if (
    data.locationType === WORK_JOURNEY_PLACE_TYPE.PRESENTIAL &&
    !data.address?.id
  ) {
    errors.push(translations.locationRequired);
  }

  if (
    !data.breakMarkingType ||
    !VALID_BREAK_MARKING_TYPES.includes(data.breakMarkingType as (typeof VALID_BREAK_MARKING_TYPES)[number])
  ) {
    errors.push(translations.breakMarkingTypeRequired);
  }

  if (requiresBreakStartTime(data.type) && !hasBreakStartTime(data.breakStartTime)) {
    errors.push(translations.breakStartTimeRequired);
  }

  if (!isFiniteIntegerInRange(data.toleranceTime, NUMERIC_FIELD_MIN, NUMERIC_FIELD_MAX)) {
    errors.push(translations.toleranceTimeInvalid);
  }

  if (!isFiniteIntegerInRange(data.earlyEndTime, NUMERIC_FIELD_MIN, NUMERIC_FIELD_MAX)) {
    errors.push(translations.earlyEndTimeInvalid);
  }

  if (
    !data.workCircumstances ||
    !VALID_WORK_CIRCUMSTANCES.includes(data.workCircumstances as (typeof VALID_WORK_CIRCUMSTANCES)[number])
  ) {
    errors.push(translations.workCircumstancesRequired);
  }

  return { isValid: errors.length === 0, errors };
};
